import React, { useEffect, useState } from "react"
import Paper from "../../ui/Paper"

import { useDispatch, useSelector } from "react-redux"
import { selectUserAccountsById, totpEnroll, totpRequired } from "./slice"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import styled from "styled-components"

import Button from "../../ui/Button"

import ErrorMsg from "../../ui/ErrorMsg"
import PropTypes from "prop-types"
import { slideFromRight } from "../../ui/animations"
import ActionsFooter from "../../ui/ActionsFooter"
import ID from "../../ui/ID"
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline"
import { getTOTPEnrollmentImageURL, getTOTPSecret, verifyTOTP } from "./api"
import IDCopy from "../../ui/IDCopy"
import Text from "../../ui/Text"
import FormGroup from "../../ui/FormGroup"
import InputRow from "../../ui/InputRow"
import Input from "../../ui/Input"

const Panel = styled(Paper)`
  padding: 2rem;
  overflow-y: auto;
  width: 25vw;
  min-width: 28rem;
  max-width: 45rem;
  animation: 0.3s ${slideFromRight} ease-out;
`
const FormContainer = styled.div``

const ImgContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
`

/**
 * Enroll a user to requiring 2FA to log in.
 * This component drives a simple workflow:
 * 1. Confirm before enrolling (resetting user secret) - note,
 *    NOT applied / active at this time, i.e. user can still log
 *    in without 2FA.
 * 2. Show QR code / secret key to user, allowing enrollment of
 *    device or app.
 * 3. Next step: Confirm (test) that 2FA with TOTP works, by asking
 *    the user to enter the code.
 * 4. Activate (require) 2FA after all checked out OK
 */
const Enroll2FA = ({ id, handleCancel }) => {
  const accountsByID = useSelector(selectUserAccountsById)
  const account = accountsByID[id]
  const [step, setStep] = useState(1) // 1,2,3,4

  if (!account) {
    return
  }

  return (
    <Panel>
      {step === 1 && (
        <StepIntro
          account={account}
          onCancel={handleCancel}
          onProceed={() => setStep(2)}
        />
      )}
      {step === 2 && (
        <StepGetKey
          account={account}
          onCancel={handleCancel}
          onProceed={() => setStep(3)}
        />
      )}
      {step === 3 && (
        <StepTest
          account={account}
          onCancel={handleCancel}
          onProceed={() => setStep(4)}
          onBack={() => setStep(2)}
        />
      )}
      {step === 4 && <StepApply account={account} onCancel={handleCancel} />}
      {account?._saveStatus === "updating" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}

      <FormContainer>
        {account?._saveStatus == "rejected" && (
          <ErrorMsg
            msg={account._errorMessage || "Something went wrong"}
            showIcon={true}
          />
        )}
      </FormContainer>
    </Panel>
  )
}

Enroll2FA.propTypes = {
  id: PropTypes.string, // email of the account
  handleCancel: PropTypes.func, // Close panel
}

/** Step 1: Show explanation, and generate keys when user proceeds. */
const StepIntro = ({ onProceed, onCancel, account }) => {
  const dispatch = useDispatch()
  const busy = account?._saveStatus == "updating"

  // Button click: Generate keys and proceed if successful
  const onGenerate = async () => {
    await dispatch(totpEnroll({ id: account?.id }))
    onProceed()
  }
  return (
    <>
      <h2>Enroll user for 2FA</h2>
      <p>
        By proceeding, you will start the process to enroll{" "}
        <ID>{account?.id}</ID> for two-factor authentication.
      </p>
      <p>
        You will generate a key to set up an authenticator app or hardware token
        which provides Timed One-Time Passwords (TOTP) that must be entered in
        addition to Username and Password during login for this user.
      </p>
      <ActionsFooter>
        <Button onClick={onCancel}>Close</Button>
        <Button onClick={onGenerate} disabled={busy} active>
          Proceed
        </Button>
      </ActionsFooter>
    </>
  )
}
StepIntro.propTypes = {
  account: PropTypes.object.isRequired,
  onProceed: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

/** Step 2: Show key to user (QR and text) allowing to setup device*/
const StepGetKey = ({ onProceed, onCancel, account }) => {
  const [secret, setSecret] = useState(null)
  const [qrURL, setQRURL] = useState(null)
  const [busy, setBusy] = useState(null)

  // Retrieve text key
  useEffect(() => {
    // eslint-disable-next-line
    ;(async function () {
      try {
        setBusy(true)
        const keyURL = await getTOTPSecret(account?.id)
        const u = new URL(keyURL)
        setSecret({
          secret: u.searchParams.get("secret"),
          issuer: u.searchParams.get("issuer"),
          period: u.searchParams.get("period"),
          algorithm: u.searchParams.get("algorithm"),
        })
      } finally {
        setBusy(false)
      }
    })()
  }, [account?.id])

  // QR code URL (once, as this does I/O to localstorage)
  useEffect(() => {
    setQRURL(getTOTPEnrollmentImageURL(account?.id))
  }, [account?.id])

  return (
    <>
      <h2>Set up authenticator</h2>
      <p>
        Use the following key to set up an authenticator app or hardware token
        for <ID>{account?.id}</ID>:.
      </p>
      {busy && <LoadingSpinnerInline text="Fetching..." />}
      {qrURL && (
        <ImgContainer>
          <img src={qrURL} />
        </ImgContainer>
      )}
      {secret && (
        <>
          <h4>Secret Key</h4>
          <IDCopy id={secret.secret} showCopy={true} />
          <p>
            <Text small>
              ({secret.algorithm}, every {secret.period} seconds)
            </Text>
          </p>
        </>
      )}
      <p>
        Once you have set up the device or app, please proceed in order to test
        that it is generating correct Timed One-Time Passwords.
      </p>
      <ActionsFooter>
        <Button onClick={onCancel}>Close</Button>
        <Button onClick={onProceed} active>
          Proceed to test
        </Button>
      </ActionsFooter>
    </>
  )
}
StepGetKey.propTypes = {
  account: PropTypes.object.isRequired,
  onProceed: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

/** Step 3: Test authenticator code before activating. */
const StepTest = ({ onProceed, onBack, onCancel, account }) => {
  const [code, setCode] = useState("")
  const [failed, setFailed] = useState(null)
  const [success, setSuccess] = useState(null)
  const [busy, setBusy] = useState(false)

  // Verify code
  const onVerify = async () => {
    try {
      setBusy(true)
      await verifyTOTP(account?.id, code)
      setSuccess("success")
      // Auto-proceed to next step
      window.setTimeout(onProceed, 1000)
    } catch (failed) {
      setFailed(failed)
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <h2>Test Authenticator</h2>
      <p>
        Please enter an authentication code (usually 6 digits) from the device
        or app that you have set up:
      </p>
      <FormGroup>
        <InputRow>
          <Input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              setFailed(null)
              if (e.key === "Enter") {
                onVerify()
              }
            }}
            autoFocus
          />
        </InputRow>
      </FormGroup>
      {busy && <LoadingSpinnerInline text="Verifying..." />}
      {failed && (
        <ErrorMsg showIcon msg="That did not work - please try again" />
      )}
      {success != null && <Text success>That worked !</Text>}
      <ActionsFooter>
        <Button onClick={onCancel}>Close</Button>
        {failed && (
          <Button onClick={onBack} delete>
            Set up again
          </Button>
        )}
        <Button onClick={onVerify} disabled={busy} active>
          Verify
        </Button>
      </ActionsFooter>
    </>
  )
}
StepTest.propTypes = {
  account: PropTypes.object.isRequired,
  onProceed: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

/** Step 4: Apply (activate) */
const StepApply = ({ onCancel, account }) => {
  const dispatch = useDispatch()
  const busy = account?._saveStatus == "updating"
  const onActivate = async () => {
    await dispatch(totpRequired({ id: account?.id, required: true }))
    onCancel() // Close window
  }
  return (
    <>
      <h2>Activate 2FA</h2>
      <p>
        You have successfully configured and verified 2FA for{" "}
        <ID>{account?.id}</ID> - please confirm now to activate.
      </p>
      <p>
        After activation, the user will no longer be able to log in without 2FA.
      </p>
      <ActionsFooter>
        <Button onClick={onCancel}>Close</Button>
        <Button onClick={onActivate} disabled={busy} active>
          Activate 2FA
        </Button>
      </ActionsFooter>
    </>
  )
}
StepApply.propTypes = {
  account: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
}
export default Enroll2FA
